import type { ReactNode } from 'react';

/**
 * The Aurora "hero" — the gradient-washed page header used at the top
 * of high-touch routes (Reception, /me, Stations, Coordinator). Built
 * on the `.aurora-hero` decorative class from tokens.css so the wash
 * stays consistent across themes.
 *
 * Layout: eyebrow chip + h1 + optional description on the left,
 * optional `trailing` slot on the right (typically a MetricBlock grid
 * or a Ring). On small screens the trailing slot wraps below.
 */
export function Hero({
  eyebrow,
  title,
  description,
  trailing,
  children,
  className = '',
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  trailing?: ReactNode;
  /** Optional extra row below the title block (e.g. a ProgressBar). */
  children?: ReactNode;
  className?: string;
}) {
  return (
    <header
      className={`aurora-hero aurora-fade-in ${className}`}
    >
      <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          {eyebrow ? (
            <div className="inline-flex items-center gap-1.5 rounded-full bg-accent/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-accent">
              {eyebrow}
            </div>
          ) : null}
          <h1
            className="mt-3 text-3xl font-semibold tracking-[var(--heading-tracking)] text-fg sm:text-4xl"
          >
            {title}
          </h1>
          {description ? (
            <p className="mt-2 max-w-xl text-sm text-fg-muted sm:text-base">
              {description}
            </p>
          ) : null}
        </div>
        {trailing ? <div className="shrink-0">{trailing}</div> : null}
      </div>
      {children ? <div className="mt-5">{children}</div> : null}
    </header>
  );
}
